import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { DatePicker, Dropdown, Menu } from 'antd'
import Select from 'react-select'
import { useHistory } from 'react-router-dom'
import { AiOutlineDown } from 'react-icons/ai'

import Receive from '../../components/reusable/receive'
import { useListData, usePet } from '../../modules'

import RecordInsurerPayment from '../../components/drawers/recordInsurerPayment'
import InsurerSubmissionDetail from '../../components/drawers/insurerSubmissionDetail'
import InsurerLedger from '../../components/drawers/insurerLedger'

const statusOptions = [
    { label: 'All statuses', value: 'all' },
    { label: 'Draft', value: 'draft' },
    { label: 'Submitted', value: 'submitted' },
    { label: 'Accepted (SHA)', value: 'accepted' },
    { label: 'Rejected (SHA)', value: 'rejected' },
    { label: 'Partially Paid', value: 'partially_paid' },
    { label: 'Paid', value: 'paid' },
    { label: 'Disputed', value: 'disputed' },
    { label: 'Written Off', value: 'written_off' },
]

const bucketOrder = ['current', '30', '60', '90', '90+']

const Summary = () => {
    const history = useHistory()

    const [string, setString] = useState('')
    const [selected, setSelected] = useState()
    const [detailsVisible, setDetailsVisible] = useState(false)
    const [payVisible, setPayVisible] = useState(false)
    const [ledgerVisible, setLedgerVisible] = useState(false)
    const [ledgerInsurer, setLedgerInsurer] = useState()
    const [selectedInsurer, setSelectedInsurer] = useState({ label: 'All insurers', value: 'all' })
    const [selectedScheme, setSelectedScheme] = useState({ label: 'All schemes', value: 'all' })
    const [selectedStatus, setSelectedStatus] = useState(statusOptions[0])
    const [asOf, setAsOf] = useState(moment())

    const [
        { loading, error, fetch },
        { list: entries = [], count = 0, data },
        { navigate, Paginate, page = 1, limit = 10 }
    ] = useListData('insurerArSummary', {
        string,
        insurer: selectedInsurer?.value !== 'all' ? selectedInsurer?.value : undefined,
        scheme: selectedScheme?.value !== 'all' ? selectedScheme?.value : undefined,
        status: selectedStatus?.value ?? 'all',
        asOf: asOf?._d ?? asOf,
    }, { notify: true, manual: true }, 'entries')

    const [{ data: insurersData }] = usePet('insuranceCompanies', { limit: 100 }, { notify: false })
    const [{ data: schemesData }] = usePet('schemes', { active: true, limit: 200 }, { notify: false })

    const insurerOptions = [
        { label: 'All insurers', value: 'all' },
        ...((insurersData?.insurances ?? []).map(insurer => ({
            label: insurer.name,
            value: insurer._id,
            isSha: insurer?.isSha,
        })))
    ]

    const schemeOptions = [
        { label: 'All schemes', value: 'all' },
        ...((schemesData?.schemes ?? [])
            .filter(scheme => selectedInsurer?.value === 'all' || String(scheme?.operatedBy?._id) === String(selectedInsurer?.value))
            .map(scheme => ({
                label: scheme.name,
                value: scheme._id,
                insurer: scheme?.operatedBy?._id,
            })))
    ]

    useEffect(() => {
        fetch()
    }, [page]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedScheme?.value === 'all') return

        const match = schemeOptions.find(option => option.value === selectedScheme?.value)
        if (!match) setSelectedScheme({ label: 'All schemes', value: 'all' })
    }, [selectedInsurer?.value, schemesData]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        navigate(1)
        fetch()
    }, [string, selectedInsurer?.value, selectedScheme?.value, selectedStatus?.value, asOf?.valueOf?.() ?? asOf]) // eslint-disable-line react-hooks/exhaustive-deps

    const summaries = data?.summaries ?? []
    const schemeSummaries = data?.schemeSummaries ?? []
    const methodology = data?.methodology

    const openDetails = entry => {
        if (entry?.source === 'sha') {
            history.push(entry?.deepLink ?? '/app/accounts/sales/sha-claims')
            return
        }

        setSelected(entry)
        setDetailsVisible(true)
    }

    const openPayment = entry => {
        if (entry?.source === 'sha') {
            history.push(entry?.deepLink ?? '/app/accounts/sales/sha-claims')
            return
        }

        setSelected(entry)
        setPayVisible(true)
    }

    const openLedger = insurerData => {
        setLedgerInsurer(insurerData)
        setLedgerVisible(true)
    }

    const totalOutstanding = summaries.reduce((total, summary) => total + Number(summary?.totalOutstanding ?? 0), 0)

    return (
        <div className="w-full mx-auto">
            {
                detailsVisible &&
                <InsurerSubmissionDetail
                    visible={ detailsVisible }
                    id={ selected?.id }
                    onClose={ () => {
                        setDetailsVisible(false)
                        setSelected(null)
                    } }
                    onPerform={ fetch } />
            }
            {
                payVisible &&
                <RecordInsurerPayment
                    visible={ payVisible }
                    submissions={ selected ? [selected] : [] }
                    insurer={ selected?.insurerData }
                    onClose={ () => {
                        setPayVisible(false)
                        setSelected(null)
                    } }
                    onPerform={ fetch } />
            }
            {
                ledgerVisible &&
                <InsurerLedger
                    visible={ ledgerVisible }
                    insurer={ ledgerInsurer }
                    onClose={ () => {
                        setLedgerVisible(false)
                        setLedgerInsurer(null)
                    } } />
            }
            <div className="sm:flex sm:justify-between sm:items-start mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 mb-1">Credit Control</h1>
                    <p className="text-sm text-gray-500 mb-0">Track every insurer's outstanding balance, aging, and submission outcomes in one place.</p>
                </div>
                <div className="text-right">
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Total outstanding</p>
                    <p className="text-2xl font-semibold mb-0">KSh {Number(totalOutstanding).toLocaleString()}</p>
                </div>
            </div>

            {
                methodology &&
                <div className="mb-4 rounded-sm border border-yellow-200 bg-yellow-50 px-4 py-3">
                    <p className="text-sm text-yellow-800 mb-1">Methodology note</p>
                    <p className="text-xs text-yellow-800 mb-0">{methodology.note}</p>
                </div>
            }

            <div className="bg-white shadow-lg rounded-sm border border-gray-200 p-4 mb-4">
                <div className="grid gap-3 md:grid-cols-5 sm:grid-cols-1">
                    <input
                        value={ string }
                        onChange={ e => setString(e.target.value) }
                        className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        type="text"
                        placeholder="Search patient, reference" />
                    <Select
                        value={ selectedInsurer }
                        onChange={ setSelectedInsurer }
                        options={ insurerOptions }
                        placeholder="Select insurer" />
                    <Select
                        value={ selectedScheme }
                        onChange={ setSelectedScheme }
                        options={ schemeOptions }
                        placeholder="Select scheme" />
                    <Select
                        value={ selectedStatus }
                        onChange={ setSelectedStatus }
                        options={ statusOptions }
                        placeholder="Select status" />
                    <DatePicker
                        className="w-full"
                        value={ asOf }
                        onChange={ value => setAsOf(value ?? moment()) } />
                </div>
            </div>

            {
                summaries.length > 0 &&
                <div className="grid gap-4 md:grid-cols-3 sm:grid-cols-1 mb-5">
                    {
                        summaries.slice(0, 6).map(summary =>
                            <div
                                key={ summary?.insurer ?? summary?.name }
                                className={`rounded-sm border px-4 py-4 text-left shadow-sm ${selectedInsurer?.value === summary?.insurer ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                                <button
                                    onClick={ () => setSelectedInsurer({ label: summary?.name, value: summary?.insurer ?? 'all' }) }
                                    className="w-full text-left">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-sm font-semibold mb-0">{summary?.name}</p>
                                        {
                                            summary?.insurerData?.isSha &&
                                            <span className="text-[10px] uppercase font-semibold text-blue-600 bg-blue-100 rounded-full px-2 py-0.5">SHA</span>
                                        }
                                    </div>
                                    <p className="text-xs text-gray-500 mb-2">{summary?.entries} entr{summary?.entries === 1 ? 'y' : 'ies'}</p>
                                    <p className="text-lg font-semibold mb-3">KSh {Number(summary?.totalOutstanding ?? 0).toLocaleString()}</p>
                                    <div className="grid grid-cols-5 gap-1">
                                        {
                                            bucketOrder.map(bucket =>
                                                <div key={ bucket } className="rounded bg-gray-100 px-2 py-1">
                                                    <p className="text-[10px] uppercase text-gray-500 mb-1">{bucket}</p>
                                                    <p className="text-xs font-medium mb-0">{Number(summary?.buckets?.[bucket] ?? 0).toLocaleString()}</p>
                                                </div>
                                            )
                                        }
                                    </div>
                                </button>
                                {
                                    summary?.insurer &&
                                    <button
                                        onClick={ () => openLedger(summary?.insurerData) }
                                        className="mt-3 text-xs font-medium text-blue-600 hover:underline">
                                        View Ledger
                                    </button>
                                }
                            </div>
                        )
                    }
                </div>
            }

            {
                schemeSummaries.length > 0 &&
                <div className="mb-5">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <p className="text-sm font-semibold text-gray-800 mb-1">Scheme breakdown</p>
                            <p className="text-xs text-gray-500 mb-0">
                                {selectedInsurer?.value === 'all'
                                    ? 'Top claim balances across schemes'
                                    : `Schemes under ${selectedInsurer?.label}`}
                            </p>
                        </div>
                    </div>
                    <div className="grid gap-4 md:grid-cols-3 sm:grid-cols-1">
                        {
                            schemeSummaries.slice(0, 6).map(summary =>
                                <button
                                    key={ summary?.scheme ?? summary?.name }
                                    onClick={ () => setSelectedScheme({ label: summary?.name, value: summary?.scheme ?? 'all' }) }
                                    className={`rounded-sm border px-4 py-4 text-left shadow-sm ${selectedScheme?.value === summary?.scheme ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white'}`}>
                                    <p className="text-sm font-semibold mb-1">{summary?.name}</p>
                                    <p className="text-xs text-gray-500 mb-2">{summary?.entries} entr{summary?.entries === 1 ? 'y' : 'ies'}</p>
                                    <p className="text-lg font-semibold mb-3">KSh {Number(summary?.totalOutstanding ?? 0).toLocaleString()}</p>
                                    <div className="grid grid-cols-5 gap-1">
                                        {
                                            bucketOrder.map(bucket =>
                                                <div key={ bucket } className="rounded bg-gray-100 px-2 py-1">
                                                    <p className="text-[10px] uppercase text-gray-500 mb-1">{bucket}</p>
                                                    <p className="text-xs font-medium mb-0">{Number(summary?.buckets?.[bucket] ?? 0).toLocaleString()}</p>
                                                </div>
                                            )
                                        }
                                    </div>
                                </button>
                            )
                        }
                    </div>
                </div>
            }

            <div className="col-span-full xl:col-span-8 bg-white shadow-lg rounded-sm border border-gray-200">
                <header className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="font-semibold text-gray-800">Worklist</h2>
                    <p className="text-sm text-gray-500 mb-0">{count} entr{count === 1 ? 'y' : 'ies'}</p>
                </header>
                <div className="p-3">
                    <Receive progress loading={ loading } error={ error }>
                        {
                            entries.length > 0 ?
                                <div className="overflow-x-auto">
                                    <table className="table-auto w-full">
                                        <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                            <tr>
                                                <th className="p-2"><div className="font-semibold text-center">No</div></th>
                                                <th className="p-2"><div className="font-semibold text-left">Patient</div></th>
                                                <th className="p-2"><div className="font-semibold text-left">Payer</div></th>
                                                <th className="p-2"><div className="font-semibold text-center">Amount</div></th>
                                                <th className="p-2"><div className="font-semibold text-center">Aging</div></th>
                                                <th className="p-2"><div className="font-semibold text-center">Status</div></th>
                                                <th className="p-2"><div className="font-semibold text-center">Source</div></th>
                                                <th className="p-2"><div className="font-semibold text-center"></div></th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-sm font-medium divide-y divide-gray-100">
                                            {
                                                entries.map((entry, key) =>
                                                    <tr key={ entry.id }>
                                                        <td className="p-2 text-center">{((page - 1) * limit) + key + 1}.</td>
                                                        <td className="p-2">
                                                            <div>{entry?.patientData?.name ?? 'Unknown patient'}</div>
                                                        </td>
                                                        <td className="p-2">
                                                            <div>{entry?.insurerData?.name ?? '—'}</div>
                                                            <p className="text-gray-500 my-0 text-xs italic">{entry?.schemeData?.name ?? 'No scheme'}</p>
                                                        </td>
                                                        <td className="p-2 text-center">KSh {Number(entry?.outstandingAmount ?? 0).toLocaleString()}</td>
                                                        <td className="p-2 text-center">
                                                            <div>{entry?.agingBucket ?? 'current'}</div>
                                                            <p className="text-gray-500 my-0 text-xs italic">{entry?.ageDays ?? 0} day(s)</p>
                                                        </td>
                                                        <td className="p-2 text-center">
                                                            <span className={`inline-flex font-medium rounded-full px-2.5 py-0.5 ${entry?.status === 'accepted' || entry?.status === 'paid' ? 'bg-green-100 text-green-700' : entry?.status === 'submitted' || entry?.status === 'partially_paid' ? 'bg-yellow-100 text-yellow-700' : entry?.status === 'rejected' || entry?.status === 'disputed' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
                                                                {entry?.status ?? 'draft'}
                                                            </span>
                                                        </td>
                                                        <td className="p-2 text-center">
                                                            <span className="text-xs uppercase text-gray-500">{entry?.source === 'sha' ? 'SHA' : 'Insurer'}</span>
                                                        </td>
                                                        <td className="p-2 text-center">
                                                            <Dropdown
                                                                overlay={
                                                                    <Menu>
                                                                        <Menu.Item onClick={ () => openDetails(entry) }>View Details</Menu.Item>
                                                                        <Menu.Item
                                                                            onClick={ () => openPayment(entry) }>
                                                                            { entry?.source === 'sha' ? 'Open SHA Remittance Workbench' : 'Record Payment' }
                                                                        </Menu.Item>
                                                                    </Menu>
                                                                }>
                                                                <button type="button" className="ant-dropdown-link flex items-center h-10" onClick={ e => e.preventDefault() }>
                                                                    <span className="mr-2">Actions</span> <AiOutlineDown />
                                                                </button>
                                                            </Dropdown>
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                                :
                                <div className="w-full h-16 flex items-center justify-center">
                                    <p className="font-semibold text-base">No balances were found for the selected filters</p>
                                </div>
                        }
                    </Receive>
                    {Paginate}
                </div>
            </div>
        </div>
    )
}

export default Summary
